import React, { useState, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import Layout from "../components/Layout";
import KfsList from "../components/grants/KfsList";
import ModuleMovedBanner from "../components/ModuleMovedBanner";
import apiClient from "../services/api";
import { useHotel } from "../contexts/HotelContext";
import {
  KFS_CALLS_HREF,
  KFS_SNAPSHOT_LABEL,
} from "../constants/legacyGrantsRoutes";

interface LocationFilters {
  province?: string;
  officeDistrict?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: string;
  viewMode?: "admin" | "user";
}

interface SortConfig {
  column: "publicationDate" | "applicationStartDate" | "applicationEndDate";
  order: "ASC" | "DESC";
}

interface Location {
  province: string;
  provinceName: string;
  count?: number;
}

const GrantsKfs: React.FC = () => {
  const [searchParams] = useSearchParams();
  const showDebugInfo = searchParams.has("debug");
  const [filters, setFilters] = useState<LocationFilters>({
    page: 1,
    limit: 50,
  });
  const [currentSort, setCurrentSort] = useState<SortConfig | null>({
    column: "publicationDate",
    order: "DESC",
  });
  const { user } = useHotel();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["grants-kfs", filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.province) params.append("province", filters.province);
      if (filters.officeDistrict)
        params.append("officeDistrict", filters.officeDistrict);
      if (filters.page) params.append("page", filters.page.toString());
      if (filters.limit) params.append("limit", filters.limit.toString());
      if (filters.sortBy) params.append("sortBy", filters.sortBy);
      if (filters.sortOrder) params.append("sortOrder", filters.sortOrder);
      if (filters.viewMode) params.append("viewMode", filters.viewMode);

      const { data } = await apiClient.get(`/kfs/articles/list?${params}`);
      return data;
    },
  });

  const { data: locations } = useQuery<Location[]>({
    queryKey: ["grants-kfs-locations"],
    queryFn: async () => {
      const { data } = await apiClient.get("/kfs/locations");
      return data;
    },
  });

  // Backend now handles filtering based on user role, so we just use the articles directly
  const filteredKfsArticles = data?.articles || [];

  const handleProvinceChange = (province: string) => {
    setFilters((prev) => ({ ...prev, province, page: 1 }));
  };

  const handleOfficeChange = (officeDistrict: string | undefined) => {
    setFilters((prev) => ({ ...prev, officeDistrict, page: 1 }));
  };

  const handleProvinceClick = (provinceName: string) => {
    // Find the province code from the province name
    const location = locations?.find(
      (loc) => loc.provinceName === provinceName
    );
    const provinceCode = location?.province || provinceName;
    setFilters((prev) => ({ ...prev, province: provinceCode, page: 1 }));
  };

  const handlePageChange = (newPage: number) => {
    setFilters((prev) => ({ ...prev, page: newPage }));
  };

  const handleLimitChange = (newLimit: number) => {
    setFilters((prev) => ({ ...prev, limit: newLimit, page: 1 })); // Reset to page 1 when limit changes
  };

  const handleSortChange = (sortBy: string, sortOrder: string) => {
    if (sortBy === "" && sortOrder === "") {
      // Reset to default sorting (publicationDate DESC)
      setFilters((prev) => ({
        ...prev,
        sortBy: undefined,
        sortOrder: undefined,
        page: 1,
      }));
      setCurrentSort({
        column: "publicationDate",
        order: "DESC",
      });
    } else {
      // Apply new sorting
      setFilters((prev) => ({
        ...prev,
        sortBy,
        sortOrder,
        page: 1,
      }));
      setCurrentSort({
        column: sortBy as
          | "publicationDate"
          | "applicationStartDate"
          | "applicationEndDate",
        order: sortOrder as "ASC" | "DESC",
      });
    }
  };

  const handleViewModeChange = (viewMode: "admin" | "user" | undefined) => {
    setFilters((prev) => ({ ...prev, viewMode, page: 1 }));
  };

  const refreshArticles = useCallback(async () => {
    await refetch();
  }, [refetch]);

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">
            Nabory KFS - Krajowy Fundusz Szkoleniowy
          </h1>
          <p className="mt-1 text-sm text-neutral-600">
            Aktualności związane z naborami Krajowego Funduszu Szkoleniowego
          </p>
        </div>

        <ModuleMovedBanner
          description="Ta strona zawiera dane historyczne — nie jest już aktualizowana. Aktualne nabory KFS znajdziesz w nowym module Naborów."
          ctaLabel="Przejdź do aktualnych naborów KFS"
          ctaHref={KFS_CALLS_HREF}
          dataSnapshotLabel={KFS_SNAPSHOT_LABEL}
        />

        <div data-testid="grants-table-container" className="bg-white rounded-lg shadow-sm border border-neutral-200">
          {isLoading ? (
            <div className="p-6 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-2 text-sm text-neutral-600">
                Ładowanie naborów KFS...
              </p>
            </div>
          ) : error ? (
            <div className="p-6 text-center text-red-600">
              <p>Błąd podczas pobierania naborów KFS</p>
            </div>
          ) : (
          <KfsList
            kfsArticles={filteredKfsArticles}
            pagination={data?.pagination}
            onProvinceClick={handleProvinceClick}
            onPageChange={handlePageChange}
            onLimitChange={handleLimitChange}
            locations={locations}
            selectedProvince={filters.province}
            onProvinceChange={handleProvinceChange}
            officeCounts={data?.officeCounts}
            selectedOfficeDistrict={filters.officeDistrict}
            onOfficeChange={handleOfficeChange}
            userRole={user?.role}
            onSortChange={handleSortChange}
            currentSort={currentSort}
            officesLabourData={data?.officesLabourData}
            onArticlesRefresh={refreshArticles}
            showDebugInfo={showDebugInfo}
            viewMode={filters.viewMode}
            onViewModeChange={handleViewModeChange}
          />
          )}
        </div>
      </div>
    </Layout>
  );
};

export default GrantsKfs;
